// src/lib/utils/logicEngine.ts
/**
 * Shared logic evaluation engine for dynamic forms.
 * Used by React (LiveForm), Vanilla JS (kiroku-form.ts), and server validation.
 */
import type {
  LogicCondition,
  LogicOperator,
  TypedFormField,
  FormFieldDefinition,
} from "../types";

// Helper to safe string
function safeString(val: unknown): string {
  return val === null || val === undefined ? "" : String(val);
}

/*
 * Normalise a held answer into a list of options.
 *
 * Every element is trimmed whichever way it arrived. The comma path always
 * trimmed and the real-array path never did, so before task 1.2 one operator
 * answered the whitespace question two different ways depending on whether a
 * multi-select reached it as an array or as a stored CSV. A whitespace-only
 * string is an empty selection for the same reason `" "` equals `""` below.
 */
function toArray(val: unknown): string[] {
  if (Array.isArray(val)) return val.map((v) => safeString(v).trim());
  const str = safeString(val).trim();
  return str ? str.split(",").map((s) => s.trim()) : [];
}

/*
 * Number() with one exception: a Symbol makes it throw
 * ("Cannot convert a Symbol value to a number"), and it is the only input in
 * the language that does. Everything else coerces, including undefined (NaN),
 * null (0), booleans and single-element arrays, so this preserves every
 * comparison a form can actually produce while keeping `compareValue` total.
 * Found by the task 1.1 spot check sweeping all seven operators over junk.
 */
function toNumber(val: unknown): number {
  return typeof val === "symbol" ? NaN : Number(val);
}

// Helper to convert time string (HH:MM) to minutes for comparison
function timeToMinutes(timeStr: string): number {
  if (!timeStr || typeof timeStr !== "string" || !timeStr.includes(":"))
    return NaN;
  const parts = timeStr.split(":").map(Number);
  return parts[0] * 60 + (parts[1] || 0);
}

/**
 * Is this a rule at all?
 *
 * `logic` is a Json column, so the `conditions: LogicCondition[]` in the type
 * is a claim about the data rather than a guarantee about it: a row written by
 * an older builder, an API client or a template can carry `{action, conditionType}`
 * with no conditions, or a null one. Every reader has to agree on what that
 * means, and the answer set here by `evaluateFieldVisibility` is that it is not
 * a rule and the field is visible. Anything that iterates `conditions` without
 * asking this throws on that row.
 *
 * Generic over the logic type for the same reason `compareValue` widens its
 * `operator` (task 1.3): the html-mode embed parses its rules out of a
 * `data-kiroku-logic` attribute and declares its own `operator: string`, so
 * demanding this module's `FieldLogic` would have kept it on a private copy of
 * the predicate. The question asked here is structural and does not depend on
 * what an operator is called.
 */
export function hasLogicRules<T extends { conditions?: unknown }>(
  logic: T | null | undefined,
): logic is T & { conditions: NonNullable<T["conditions"]> } {
  return Array.isArray(logic?.conditions) && logic.conditions.length > 0;
}

/**
 * Compare one already-read answer against one rule operand.
 *
 * This is the only place an operator is implemented. The two renderers differ
 * in exactly one thing, where the value comes from: React reads it out of
 * state, the vanilla embed reads it off a rendered `<input>`. Value
 * acquisition is genuinely per-renderer and cannot be shared; the comparison
 * never was renderer-specific, and was duplicated only because it was written
 * next to the value-reading. That duplication is `bug-037`.
 *
 * Total by construction: it touches no DOM, no React and no Prisma, every
 * branch returns a boolean, and an operator it does not recognise returns
 * false. False, not true, because a rule stored with a typo should leave a
 * field hidden rather than expose one somebody meant to gate.
 *
 * `operator` is widened past `LogicOperator` on purpose: `FormField.logic` is
 * a Json column and the embed parses it out of a `data-kiroku-logic`
 * attribute, so the value that arrives here is whatever was stored, not
 * whatever the type says.
 *
 * ## Whitespace: `equals` and `not_equals` trim both sides (task 1.2)
 *
 * Three reasons, in the order they decided it. Both embeds have trimmed since
 * they were written (`kiroku-form.ts`, `kiroku-embed.ts`) while this engine did
 * not, so the *only* choice that breaks no rule working in production today is
 * to trim: deciding the other way would silently stop matching for every
 * embedded form whose stored operand has a stray space. An owner who types
 * `"Yes "` into the logic editor means the answer, not the whitespace, and
 * nothing in the builder shows them the difference. And a visitor's answer is
 * whatever an `<input>` handed back, which is not a place trailing spaces are
 * meaningful.
 *
 * The decision stops at the equality operators, deliberately:
 *
 * - `contains` is a substring test, so whitespace inside the operand is part
 *   of the query (`"o W"` is a real search). All three renderers already agree
 *   not to trim it, so there is no divergence to fix and trimming a one-space
 *   operand into `""` would match every answer instead of none.
 * - `is_empty` / `is_not_empty` ask whether there is an answer at all, not
 *   what it says, so `"   "` stays a non-empty answer.
 * - `greater_than` / `less_than` need nothing: `Number()` and `timeToMinutes`
 *   both ignore surrounding whitespace already.
 *
 * Pinned by `tests/logic-engine.test.ts`, whose
 * "whitespace decision (task 1.2)" block asserts where the trimming stops.
 */
export function compareValue(
  operator: LogicOperator | (string & {}),
  fieldValue: unknown,
  conditionValue: unknown,
): boolean {
  switch (operator) {
    case "equals": {
      if (Array.isArray(fieldValue) || Array.isArray(conditionValue)) {
        const valArr = toArray(fieldValue);
        const targetArr = toArray(conditionValue);
        if (valArr.length !== targetArr.length) return false;
        return valArr.every((v) => targetArr.includes(v));
      }
      return (
        safeString(fieldValue).trim() === safeString(conditionValue).trim()
      );
    }

    case "not_equals": {
      if (Array.isArray(fieldValue) || Array.isArray(conditionValue)) {
        const valArr = toArray(fieldValue);
        const targetArr = toArray(conditionValue);
        if (valArr.length !== targetArr.length) return true;
        return !valArr.every((v) => targetArr.includes(v));
      }
      return (
        safeString(fieldValue).trim() !== safeString(conditionValue).trim()
      );
    }

    case "contains": {
      if (Array.isArray(fieldValue)) {
        return fieldValue.some((v) =>
          safeString(v)
            .toLowerCase()
            .includes(safeString(conditionValue).toLowerCase()),
        );
      }
      return safeString(fieldValue)
        .toLowerCase()
        .includes(safeString(conditionValue).toLowerCase());
    }

    case "is_empty":
      return (
        fieldValue === undefined ||
        fieldValue === null ||
        fieldValue === "" ||
        (Array.isArray(fieldValue) && fieldValue.length === 0)
      );

    case "is_not_empty":
      return (
        fieldValue !== undefined &&
        fieldValue !== null &&
        fieldValue !== "" &&
        !(Array.isArray(fieldValue) && fieldValue.length === 0)
      );

    case "greater_than": {
      const rStr = String(fieldValue);
      const tStr = String(conditionValue);
      // Check if both look like time strings
      if (rStr.includes(":") && tStr.includes(":")) {
        return timeToMinutes(rStr) > timeToMinutes(tStr);
      }
      return toNumber(fieldValue) > toNumber(conditionValue);
    }

    case "less_than": {
      const rStr = String(fieldValue);
      const tStr = String(conditionValue);
      if (rStr.includes(":") && tStr.includes(":")) {
        return timeToMinutes(rStr) < timeToMinutes(tStr);
      }
      return toNumber(fieldValue) < toNumber(conditionValue);
    }

    default:
      return false;
  }
}

/**
 * Read the trigger field's answer out of React state, then compare it.
 *
 * The lookup is the renderer-specific half; `compareValue` is the shared half.
 * A condition naming a field that is not on the form resolves false rather
 * than throwing, so one stale rule cannot take down the whole engine.
 */
export function evaluateCondition(
  condition: LogicCondition,
  values: Record<string, unknown>,
  fieldIdToName: Map<string, string>,
): boolean {
  const fieldName = fieldIdToName.get(condition.fieldId);

  if (!fieldName) {
    console.warn(
      `[LogicEngine] Warning: Could not find name for field ID: ${condition.fieldId}`,
    );
    return false;
  }

  return compareValue(condition.operator, values[fieldName], condition.value);
}

/**
 * Evaluates whether a field should be visible based on its logic rules.
 * Returns true if the field should be shown.
 */
export function evaluateFieldVisibility(
  field: FormFieldDefinition,
  values: Record<string, unknown>,
  allFields: FormFieldDefinition[],
): boolean {
  // If no logic is defined, the field is visible by default
  if (!hasLogicRules(field.logic)) {
    return true;
  }

  const { action, conditionType, conditions } = field.logic;

  // Create a map of field IDs to names for the evaluator
  const fieldIdToName = new Map<string, string>(
    allFields.map((f) => [f.id, f.name]),
  );

  // Debug: Check if the map actually contains the IDs we need
  // const missingIds = conditions.filter(c => !fieldIdToName.has(c.fieldId));
  // if (missingIds.length > 0) {
  //   console.error(`[LogicEngine] Broken References in field '${field.name}':`, missingIds);
  // }

  // Use the shared evaluateCondition helper for consistent results
  const results = conditions.map((condition) =>
    evaluateCondition(condition, values, fieldIdToName),
  );

  // Check if conditions are met based on 'all' vs 'any'
  const conditionsMet =
    conditionType === "all" ? results.every((r) => r) : results.some((r) => r);

  // If action is 'show', show if conditions met. If action is 'hide', hide if conditions met.
  const isVisible = action === "show" ? conditionsMet : !conditionsMet;

  // Console log only when logic actually runs to reduce noise
  // console.log(`[LogicEngine] Field: ${field.name}, Visible: ${isVisible}, ConditionsMet: ${conditionsMet}`);

  return isVisible;
}

/**
 * Filters fields to only those that are currently visible.
 */
export function getVisibleFields(
  fields: TypedFormField[],
  values: Record<string, unknown>,
): TypedFormField[] {
  return fields.filter((field) =>
    evaluateFieldVisibility(field, values, fields),
  );
}
/**
 * Builds a dependency map: trigger field ID -> dependent field IDs.
 * Used by the JS client to know which fields to re-evaluate on change.
 */
export function buildDependencyMap(
  fields: TypedFormField[],
): Map<string, string[]> {
  const map = new Map<string, string[]>();

  for (const field of fields) {
    if (!hasLogicRules(field.logic)) continue;

    for (const condition of field.logic.conditions) {
      const triggerId = condition.fieldId;
      if (!map.has(triggerId)) {
        map.set(triggerId, []);
      }
      map.get(triggerId)!.push(field.id);
    }
  }

  return map;
}

/**
 * Detects circular dependencies in logic rules.
 * Returns array of field IDs involved in cycles, or empty if none.
 */
export function detectCircularDependencies(fields: TypedFormField[]): string[] {
  const graph = new Map<string, Set<string>>();

  for (const field of fields) {
    if (!hasLogicRules(field.logic)) continue;
    const deps = new Set(field.logic.conditions.map((c) => c.fieldId));
    graph.set(field.id, deps);
  }

  const visited = new Set<string>();
  const recursionStack = new Set<string>();
  const cycles: string[] = [];

  function dfs(nodeId: string): boolean {
    visited.add(nodeId);
    recursionStack.add(nodeId);

    const dependencies = graph.get(nodeId) || new Set();
    for (const depId of dependencies) {
      if (!visited.has(depId)) {
        if (dfs(depId)) return true;
      } else if (recursionStack.has(depId)) {
        cycles.push(nodeId);
        return true;
      }
    }

    recursionStack.delete(nodeId);
    return false;
  }

  for (const field of fields) {
    if (field.logic && !visited.has(field.id)) {
      dfs(field.id);
    }
  }

  return cycles;
}
